/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";

import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";

import { Grid, Box, Tooltip } from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";

import TextField from "@mui/material/TextField";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

import { title as titlingFunction } from "../../utils/Functions";
import { modifyField } from "./formControlsFunctions";

import { Effect } from "../../utils/Interfaces";
import { useAppDispatch } from "../../app/hooks";
import { Cancel, CheckCircle } from "@mui/icons-material";

export interface MultilineEditableFieldProps<Model> {
    title: string;
    body: string;
    updatePath: string;
    updateKey: keyof Model;
    lines: number;
    editable: boolean;
    onUpdated?: Effect;
}

const MultilineEditableField = <ParentModel,>(props: MultilineEditableFieldProps<ParentModel>) => {
    const { title, body, updatePath, updateKey, lines, editable, onUpdated } = props;

    const [editing, setEditing] = useState<boolean>(false);
    const [bodyState, setBodyState] = useState<string>(body);

    const dispatch = useAppDispatch();

    const darkTheme: boolean = localStorage.getItem("techwiseTheme") === "dark";

    let disabledComp = editable ?? true;

    const onCancel = () => {
        setBodyState(body);
        setEditing(false);
    };

    return (
        <CardContent sx={{ background: darkTheme ? "#333333" : "#EEEEEE", borderRadius: 2, mb: 1 }}>
            <Grid container direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: -1 }}>
                <Typography variant="h6">{title}</Typography>

                {editing && (
                    <Box>
                        <Tooltip title="Cancel Changes">
                            <IconButton onClick={onCancel}>
                                <Cancel />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Save Changes">
                            <IconButton
                                color="primary"
                                onClick={() => {
                                    modifyField<ParentModel>(updatePath, updateKey, bodyState, dispatch, () => {
                                        if (onUpdated) {
                                            onUpdated();
                                        }
                                        setEditing(false);
                                    });
                                }}
                            >
                                <CheckCircle />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}

                {!editing && disabledComp && (
                    <Tooltip title={`Edit ${title}`}>
                        <IconButton onClick={() => setEditing(true)} size="large">
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Grid>

            {editing && (
                <TextField
                    multiline
                    rows={4}
                    fullWidth
                    margin="normal"
                    id={`${updateKey.toString()}`}
                    name={`${updateKey.toString()}`}
                    label={titlingFunction(`${updateKey.toString()}`.replace("_", " "))}
                    value={bodyState}
                    onChange={(event) => setBodyState(event.target.value)}
                />
            )}

            {!editing && (
                <>
                    {!(lines > 1) && (
                        <Typography color="text.secondary">
                            {bodyState === null || bodyState === "" ? "--" : bodyState}
                        </Typography>
                    )}

                    {lines > 1 && bodyState.includes("\n") && (
                        <Typography color="text.secondary" component="span">
                            <pre>{bodyState === "" ? "--" : bodyState}</pre>
                        </Typography>
                    )}

                    {lines > 1 && !bodyState.includes("\n") && (
                        <Typography color="text.secondary" component="span">
                            {bodyState === "" ? "--" : bodyState}
                        </Typography>
                    )}
                </>
            )}
        </CardContent>
    );
};

export default MultilineEditableField;
